class PredictiveSearch extends SearchForm {
  static nextId = 0;

  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.instanceId = `ha-predictive-${++PredictiveSearch.nextId}`;
    this.searchTerm = '';
    this.requestId = 0;
    this.isOpen = false;
    this.dismissed = false;
    this.input.form.addEventListener('submit', (event) => {
      if (!this.getQuery()) event.preventDefault();
    });
    this.input.addEventListener('input', () => {
      this.dismissed = false;
      this.cancelRequest();
      this.removeAttribute('results');
      this.closeResults();
    });
    this.input.addEventListener('focus', () => this.onFocus());
    this.addEventListener('focusout', () => setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    }, 0));
    this.addEventListener('keydown', (event) => this.onKeydown(event));
    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-predictive-close]')) {
        this.input.focus({ preventScroll: true });
        this.close();
      } else if (event.target.closest('.ha-predictive-all')) {
        event.preventDefault();
        this.input.form.requestSubmit();
      }
    });
  }

  connectedCallback() {
    this.listeners = new AbortController();
    const options = { signal: this.listeners.signal, passive: true };
    document.addEventListener('pointerdown', (event) => {
      if (!this.contains(event.target)) this.close();
    }, options);
    this.onViewportChange = () => {
      if (this.isOpen) requestAnimationFrame(() => this.positionResults());
    };
    window.addEventListener('resize', this.onViewportChange, options);
    window.addEventListener('scroll', this.onViewportChange, options);
    this.closest('.main-nav')?.addEventListener('scroll', this.onViewportChange, options);
    window.visualViewport?.addEventListener('resize', this.onViewportChange, options);
    window.visualViewport?.addEventListener('scroll', this.onViewportChange, options);
  }

  disconnectedCallback() {
    this.listeners?.abort();
    this.close();
    this.resizeObserver?.disconnect();
  }

  getQuery() { return this.input.value.trim(); }

  cancelRequest() {
    this.requestId += 1;
    this.abortController?.abort();
  }

  onChange() {
    super.onChange();
    this.searchTerm = this.getQuery();
    if (!this.searchTerm) { this.close(true); return; }
    if (this.dismissed || document.activeElement !== this.input) return;
    this.getSearchResults(this.searchTerm);
  }

  onFormReset(event) {
    event.preventDefault();
    this.querySelectorAll('[aria-selected="true"]').forEach((el) => el.setAttribute('aria-selected', 'false'));
    this.input.value = '';
    this.searchTerm = '';
    this.input.dispatchEvent(new Event('input', { bubbles: true }));
    this.input.focus({ preventScroll: true });
    this.toggleResetButton();
    this.close(true);
  }

  onFocus() {
    this.dismissed = false;
    this.toggleResetButton();
    const term = this.getQuery();
    if (!term) return;
    if (this.searchTerm === term && this.hasAttribute('results')) this.open();
    else { this.searchTerm = term; this.getSearchResults(term); }
  }

  onKeydown(event) {
    if (event.key === 'Escape') {
      // Native search inputs otherwise clear the customer's text on Escape.
      event.preventDefault();
      this.close();
      return;
    }
    if (event.target !== this.input) return;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!this.isOpen) this.onFocus();
      else this.switchOption(event.key === 'ArrowUp' ? -1 : 1);
    } else if (event.key === 'Enter' && this.isOpen) {
      const selected = this.querySelector('[aria-selected="true"]');
      if (selected) {
        event.preventDefault();
        (selected.querySelector('a') || selected).click();
      }
    }
  }

  switchOption(step) {
    const options = [...this.predictiveSearchResults.querySelectorAll('[role="option"]')]
      .filter((el) => el.checkVisibility());
    if (!options.length) return;
    const selected = this.querySelector('[aria-selected="true"]');
    const index = options.indexOf(selected);
    const nextIndex = index < 0 ? (step > 0 ? 0 : options.length - 1) : (index + step + options.length) % options.length;
    const next = options[nextIndex];
    selected?.setAttribute('aria-selected', 'false');
    next.setAttribute('aria-selected', 'true');
    this.input.setAttribute('aria-activedescendant', next.id);
    const scroller = next.closest('[data-predictive-products], .ha-predictive-routes');
    if (scroller) {
      const bounds = scroller.getBoundingClientRect(), item = next.getBoundingClientRect();
      if (item.bottom > bounds.bottom) scroller.scrollTop += item.bottom - bounds.bottom;
      else if (item.top < bounds.top) scroller.scrollTop -= bounds.top - item.top;
    }
  }

  async getSearchResults(term) {
    this.cancelRequest();
    const requestId = this.requestId;
    const key = term.toLowerCase();
    this.abortController = new AbortController();
    this.setAttribute('loading', 'true');
    this.setLiveRegionText(this.getAttribute('data-loading-text'));
    try {
      let markup = this.cachedResults[key];
      if (!markup) {
        const url = new URL(routes.predictive_search_url, location.origin);
        url.searchParams.set('q', term);
        url.searchParams.set('section_id', 'predictive-search');
        // Give products their own allowance; retain Shopify's configured resource types and ranking.
        url.searchParams.set('resources[limit]', '10');
        url.searchParams.set('resources[limit_scope]', 'each');
        const response = await fetch(url, { signal: this.abortController.signal });
        if (!response.ok) throw new Error(`Predictive search: ${response.status}`);
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        markup = doc.querySelector('#shopify-section-predictive-search')?.innerHTML;
        if (!markup) throw new Error('Predictive search section missing');
        document.querySelectorAll('predictive-search').forEach((instance) => { instance.cachedResults[key] = markup; });
      }
      if (requestId !== this.requestId || this.dismissed || this.getQuery() !== term || !this.contains(document.activeElement)) return;
      this.renderSearchResults(markup);
    } catch (error) {
      if (error.name === 'AbortError' || requestId !== this.requestId) return;
      this.close();
      // The standard search form remains available if suggestions are unavailable.
    }
  }

  setLiveRegionText(text) {
    const status = this.querySelector('.predictive-search-status');
    if (!status) return;
    status.setAttribute('aria-hidden', 'false');
    status.textContent = text || '';
  }

  renderSearchResults(markup) {
    const template = document.createElement('template');
    template.innerHTML = markup;
    const ids = new Map();
    template.content.querySelectorAll('[id]').forEach((el) => {
      const nextId = `${this.instanceId}-${el.id}`;
      ids.set(el.id, nextId);
      el.id = nextId;
    });
    template.content.querySelectorAll('[aria-labelledby]').forEach((el) => {
      el.setAttribute('aria-labelledby', el.getAttribute('aria-labelledby').split(' ').map((id) => ids.get(id) || id).join(' '));
    });
    this.predictiveSearchResults.replaceChildren(template.content);
    const allResultsText = this.querySelector('[data-predictive-search-search-for-text]');
    allResultsText.textContent = allResultsText.dataset.template.replace('__QUERY__', this.getQuery());
    const listbox = this.querySelector('[role="listbox"]');
    this.input.setAttribute('aria-controls', listbox.id);
    this.input.removeAttribute('aria-owns');
    const collections = this.querySelector('.ha-predictive-collection-links');
    if (collections && window.innerWidth < 750) collections.open = false;
    this.removeAttribute('loading');
    this.setAttribute('results', 'true');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]')?.textContent);
    this.resizeObserver?.disconnect();
    const products = this.querySelector('[data-predictive-products]');
    products.addEventListener('scroll', () => this.updateScrollHint(), { passive: true });
    this.resizeObserver = new ResizeObserver(() => this.updateScrollHint());
    this.resizeObserver.observe(products);
    this.open();
  }

  updateScrollHint() {
    const list = this.querySelector('[data-predictive-products]');
    const hint = this.querySelector('[data-predictive-scroll-hint]');
    if (list && hint) {
      hint.hidden = list.scrollHeight <= list.clientHeight + 2;
      hint.classList.toggle('is-at-end', list.scrollTop + list.clientHeight >= list.scrollHeight - 2);
    }
  }

  positionResults() {
    if (!this.isOpen) return;
    const bar = this.closest('.ha-search-catalogue__toolbar');
    const anchor = bar || this.input.form;
    const anchorBounds = anchor.getBoundingClientRect();
    const parentBounds = this.predictiveSearchResults.offsetParent.getBoundingClientRect();
    // Align with the complete frozen toolbar, including the second mobile row.
    this.predictiveSearchResults.style.top = `${anchorBounds.bottom - parentBounds.top + 6}px`;
    if (bar) this.predictiveSearchResults.style.width = `${bar.clientWidth}px`;
    const viewport = window.visualViewport;
    const bottom = (viewport?.offsetTop || 0) + (viewport?.height || window.innerHeight);
    const available = Math.max(96, Math.min(540, bottom - anchorBounds.bottom - 18));
    this.predictiveSearchResults.style.setProperty('--ha-predictive-available', `${available}px`);
    this.predictiveSearchResults.classList.toggle('ha-predictive-compact', available < 330);
    this.updateScrollHint();
  }

  open() {
    document.querySelectorAll('predictive-search[open]').forEach((instance) => {
      if (instance !== this) instance.close();
    });
    this.dismissed = false;
    this.isOpen = true;
    this.setAttribute('open', 'true');
    this.input.setAttribute('aria-expanded', 'true');
    this.positionResults();
    requestAnimationFrame(() => this.positionResults());
  }

  close(clear = false) {
    this.dismissed = true;
    this.cancelRequest();
    this.closeResults(clear);
  }

  closeResults(clear = false) {
    if (clear) { this.input.value = ''; this.removeAttribute('results'); }
    this.querySelector('[aria-selected="true"]')?.setAttribute('aria-selected', 'false');
    this.input.removeAttribute('aria-activedescendant');
    this.input.setAttribute('aria-expanded', 'false');
    this.removeAttribute('open');
    this.removeAttribute('loading');
    this.isOpen = false;
  }
}

customElements.define('predictive-search', PredictiveSearch);
